import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectIsAuthenticated, selectCurrentUser } from '../store/slices/authSlice';
import { Toast } from './Toast';
import ShareModal from './ShareModal';

function PinModal({ pinId, onClose, pins = [] }) {
  const navigate = useNavigate();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const currentUser = useSelector(selectCurrentUser);
  const token = useSelector((state) => state.auth.token);
  
  const [currentPinId, setCurrentPinId] = useState(pinId);
  const [pin, setPin] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentsEnabled, setCommentsEnabled] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  
  const [commentText, setCommentText] = useState('');
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [isLiked, setIsLiked] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [isLiking, setIsLiking] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const modalRef = useRef(null);

  const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

  // Update currentPinId when pinId prop changes
  useEffect(() => {
    setCurrentPinId(pinId);
  }, [pinId]);

  useEffect(() => {
    document.body.classList.add('overflow-hidden');
    
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    
    return () => {
      document.body.classList.remove('overflow-hidden');
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  useEffect(() => {
    loadPin();
    loadComments();
    checkIfLiked();
    checkIfSaved();
  }, [currentPinId]);

  const loadPin = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/pins/${currentPinId}`);
      if (response.ok) {
        const data = await response.json();
        setPin(data);
        setLikeCount(data.likes || 0);
      }
    } catch (error) {
      console.error('Error loading pin:', error);
      Toast.error('Không thể tải prompt');
    } finally {
      setIsLoading(false);
    }
  };

  const loadComments = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/pins/${currentPinId}/comments`);
      if (response.ok) {
        const data = await response.json();
        setComments(data.comments || []);
        setCommentsEnabled(data.commentsEnabled !== false);
      }
    } catch (error) {
      console.error('Error loading comments:', error);
    }
  };

  const checkIfLiked = async () => {
    if (!currentUser?.username) return;
    
    try {
      const response = await fetch(
        `${API_BASE_URL}/pins/${currentPinId}/liked?username=${currentUser.username}`
      );
      if (response.ok) {
        const data = await response.json();
        setIsLiked(data.isLiked);
      }
    } catch (error) {
      console.error('Error checking like status:', error);
    }
  };

  const checkIfSaved = async () => {
    if (!currentUser?.username) return;
    
    try {
      const response = await fetch(
        `${API_BASE_URL}/auth/saved-pin/${currentPinId}?username=${currentUser.username}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      if (response.ok) {
        const data = await response.json();
        setIsSaved(data.isSaved);
      }
    } catch (error) {
      console.error('Error checking save status:', error);
    }
  };

  const handleLike = async () => {
    if (!isAuthenticated) {
      Toast.warning('Vui lòng đăng nhập để thích prompt!');
      return;
    }
    
    if (isLiking) return;
    
    setIsLiking(true);
    try {
      const response = await fetch(`${API_BASE_URL}/pins/${currentPinId}/like`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ username: currentUser.username })
      });

      if (response.ok) {
        const result = await response.json();
        setIsLiked(result.isLiked);
        setLikeCount(result.likes);
        
        Toast.success(result.isLiked ? 'Đã thích prompt!' : 'Đã bỏ thích!');
        
        const likeBtn = document.getElementById('likeBtn');
        if (likeBtn) {
          likeBtn.classList.add('scale-110');
          setTimeout(() => likeBtn.classList.remove('scale-110'), 200);
        }
      } else {
        Toast.error('Không thể thích prompt này!');
      }
    } catch (error) {
      console.error('Error liking pin:', error);
      Toast.error('Không thể thích prompt này!');
    } finally {
      setTimeout(() => setIsLiking(false), 500);
    }
  };

  const handleSave = async () => {
    if (!isAuthenticated) {
      Toast.warning('Vui lòng đăng nhập để lưu prompt!');
      return;
    }
    
    if (isSaving) return;
    
    setIsSaving(true);
    try {
      const endpoint = isSaved ? 'unsave-pin' : 'save-pin';
      const response = await fetch(`${API_BASE_URL}/auth/${endpoint}/${currentPinId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setIsSaved(!isSaved);
        Toast.success(isSaved ? 'Đã bỏ lưu prompt!' : 'Đã lưu prompt!');
      } else {
        Toast.error('Không thể lưu prompt!');
      }
    } catch (error) {
      console.error('Error saving pin:', error);
      Toast.error('Không thể lưu prompt!');
    } finally {
      setTimeout(() => setIsSaving(false), 500);
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      Toast.warning('Vui lòng đăng nhập để bình luận!');
      return;
    }
    
    if (!commentText.trim()) {
      Toast.warning('Vui lòng nhập bình luận!');
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE_URL}/pins/${currentPinId}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          text: commentText.trim(),
          user: currentUser.username
        })
      });

      if (response.ok) {
        setCommentText('');
        loadComments();
        Toast.success('Đã thêm bình luận!');
      } else {
        const error = await response.json();
        Toast.error(error.error || 'Không thể thêm bình luận!');
      }
    } catch (error) {
      console.error('Error adding comment:', error);
      Toast.error('Không thể thêm bình luận!');
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Bạn có chắc muốn xóa bình luận này?')) return;
    
    try {
      const response = await fetch(
        `${API_BASE_URL}/pins/${currentPinId}/comments/${commentId}?username=${currentUser.username}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        loadComments();
        Toast.success('Đã xóa bình luận!');
      } else {
        Toast.error('Không thể xóa bình luận!');
      }
    } catch (error) {
      console.error('Error deleting comment:', error);
      Toast.error('Không thể xóa bình luận!');
    }
  };

  const copyPrompt = async () => {
    if (pin?.prompt) {
      try {
        await navigator.clipboard.writeText(pin.prompt);
        Toast.success('Đã sao chép prompt!');
        
        // Track copy
        if (currentUser?.username) {
          fetch(`${API_BASE_URL}/pins/${currentPinId}/copy`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ username: currentUser.username })
          }).catch(err => console.error('Error tracking copy:', err));
        }
      } catch (error) {
        console.error('Error copying:', error);
        Toast.error('Không thể sao chép!');
      }
    }
  };

  const shareToFacebook = () => {
    const url = `${window.location.origin}/?pinId=${currentPinId}`;
    const shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
    window.open(shareUrl, '_blank', 'width=600,height=400');
    setShowShareMenu(false);
    Toast.success('Đang mở Facebook...');
  };

  const shareToTwitter = () => {
    const text = `${pin?.title || 'Prompt'} - ${pin?.prompt?.substring(0, 100)}...`;
    const url = `${window.location.origin}/?pinId=${currentPinId}`;
    const shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
    window.open(shareUrl, '_blank', 'width=600,height=400');
    setShowShareMenu(false);
    Toast.success('Đang mở Twitter...');
  };

  const shareToLinkedIn = () => {
    const url = `${window.location.origin}/?pinId=${currentPinId}`;
    const shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
    window.open(shareUrl, '_blank', 'width=600,height=400');
    setShowShareMenu(false);
    Toast.success('Đang mở LinkedIn...');
  };

  const copyShareLink = () => {
    const url = `${window.location.origin}/?pinId=${currentPinId}`;
    navigator.clipboard.writeText(url).then(() => {
      Toast.success('Đã sao chép link!');
      setShowShareMenu(false);
    }).catch(() => {
      Toast.error('Không thể sao chép link!');
    });
  };

  const nativeShare = async () => {
    const url = `${window.location.origin}/?pinId=${currentPinId}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: pin?.title || 'Prompt',
          text: pin?.prompt?.substring(0, 200),
          url: url
        });
        setShowShareMenu(false);
        Toast.success('Đã chia sẻ!');
      } catch (error) {
        if (error.name !== 'AbortError') {
          Toast.error('Không thể chia sẻ!');
        }
      }
    } else {
      copyShareLink();
    }
  };

  const formatTimeAgo = (date) => {
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);
    
    if (seconds < 60) return 'Vừa xong';
    if (seconds < 3600) return `${Math.floor(seconds / 60)} phút trước`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)} giờ trước`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)} ngày trước`;
    if (seconds < 2592000) return `${Math.floor(seconds / 604800)} tuần trước`;
    if (seconds < 31536000) return `${Math.floor(seconds / 2592000)} tháng trước`;
    return `${Math.floor(seconds / 31536000)} năm trước`;
  };

  const filterByTag = (tag) => {
    dispatch(setTag(tag));
    onClose();
  };

  if (isLoading) {
    return (
      <div className="modal-enhanced fixed inset-0 z-[100] flex items-center justify-center p-0 sm:p-4 lg:p-10 bg-black/70 backdrop-blur-sm animate-fade-in">
        <div className="bg-white dark:bg-gray-800 rounded-3xl p-10 shadow-2xl animate-scale-in">
          <div className="relative w-16 h-16 mx-auto">
            <div className="absolute inset-0 border-4 border-blue-200 dark:border-blue-900 rounded-full"></div>
            <div className="absolute inset-0 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          </div>
          <p className="text-center mt-4 text-gray-600 dark:text-gray-400 font-medium">Đang tải...</p>
        </div>
      </div>
    );
  }

  if (!pin) return null;

  const isOwner = currentUser && currentUser.username === pin.user;

  return (
    <div
      className="modal-enhanced fixed inset-0 z-[100] flex items-center justify-center p-0 sm:p-4 lg:p-10 opacity-100 transition-opacity duration-300 bg-black/70 backdrop-blur-sm animate-fade-in"
      onClick={onClose}
    >
      <div
        ref={modalRef}
        className="modal-content-enhanced w-full h-full sm:max-w-[1400px] sm:h-auto sm:max-h-[90vh] flex flex-col lg:flex-row relative transform scale-100 transition-transform duration-300 overflow-hidden bg-white dark:bg-gray-900 sm:rounded-3xl shadow-2xl animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button with better design */}
        <button
          onClick={onClose}
          className="absolute top-4 left-4 sm:top-6 sm:left-6 z-50 w-11 h-11 sm:w-12 sm:h-12 bg-white/95 hover:bg-white dark:bg-gray-800/95 dark:hover:bg-gray-800 rounded-full flex items-center justify-center text-gray-900 dark:text-gray-100 backdrop-blur-xl transition-all shadow-xl hover:scale-110 hover:rotate-90 duration-300 border border-gray-200 dark:border-gray-700"
        >
          <span className="material-symbols-rounded text-[22px] sm:text-[24px]">close</span>
        </button>

        {/* Image Section with gradient background */}
        <div className="flex-shrink-0 bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center p-6 sm:p-8 lg:p-12 relative lg:flex-1 h-[45vh] sm:h-auto lg:border-r border-gray-800">
          <img
            src={pin.image}
            alt={pin.title}
            className="max-w-full max-h-full object-contain rounded-2xl shadow-2xl transition-all duration-500 hover:scale-105"
          />
          
          {/* Image overlay info */}
          <div className="absolute bottom-6 left-6 right-6 bg-black/40 backdrop-blur-md rounded-2xl p-4 opacity-0 hover:opacity-100 transition-opacity duration-300">
            <div className="flex items-center gap-4 text-white text-sm">
              <div className="flex items-center gap-2">
                <span className="material-symbols-rounded text-[18px]">visibility</span>
                <span className="font-semibold">{pin.views || 0} views</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="material-symbols-rounded text-[18px]">favorite</span>
                <span className="font-semibold">{likeCount} likes</span>
              </div>
            </div>
          </div>
        </div>

        {/* Content Section with better spacing */}
        <div className="flex-1 lg:w-[520px] bg-gradient-to-br from-white to-gray-50 dark:from-gray-900 dark:to-gray-800 flex flex-col overflow-hidden">
          {/* Main Content with custom scrollbar */}
          <div className="flex-1 flex flex-col px-5 sm:px-7 lg:px-9 py-5 sm:py-7 lg:py-10 overflow-y-auto custom-scrollbar">
            {/* Action Buttons with glass effect */}
            <div className="flex items-center justify-between mb-6 sm:mb-8 pb-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-2">
                {/* Like Button with animation */}
                <button
                  id="likeBtn"
                  onClick={handleLike}
                  disabled={isLiking}
                  className={`group flex items-center gap-2 px-4 py-2.5 rounded-full transition-all hover:scale-110 disabled:opacity-50 ${
                    isLiked 
                      ? 'bg-gradient-to-r from-red-500 to-pink-500 text-white shadow-lg shadow-red-500/30' 
                      : 'bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  <span 
                    className={`material-symbols-rounded text-[22px] transition-transform group-hover:scale-125 ${isLiked ? 'animate-heartbeat' : ''}`}
                    style={{
                      fontVariationSettings: isLiked ? "'FILL' 1, 'wght' 400, 'GRAD' 0, 'opsz' 24" : "'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 24"
                    }}
                  >
                    favorite
                  </span>
                  <span className="font-bold text-sm">{likeCount}</span>
                </button>
                
                {/* Copy Button */}
                <button
                  onClick={copyPrompt}
                  className="group flex items-center gap-2 px-4 py-2.5 rounded-full bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 transition-all hover:scale-110"
                >
                  <span className="material-symbols-rounded text-gray-700 dark:text-gray-300 text-[22px] group-hover:rotate-12 transition-transform">content_copy</span>
                  <span className="hidden sm:inline font-semibold text-sm text-gray-700 dark:text-gray-300">Copy</span>
                </button>

                {/* Save Button */}
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className={`group flex items-center gap-2 px-4 py-2.5 rounded-full transition-all hover:scale-110 disabled:opacity-50 ${
                    isSaved 
                      ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-lg shadow-blue-500/30' 
                      : 'bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  <span 
                    className={`material-symbols-rounded text-[22px] transition-transform group-hover:scale-125 ${isSaved ? 'animate-bounce-in' : ''}`}
                    style={{
                      fontVariationSettings: isSaved ? "'FILL' 1, 'wght' 400, 'GRAD' 0, 'opsz' 24" : "'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 24"
                    }}
                  >
                    bookmark
                  </span>
                </button>
              </div>
              
              {/* Share Button */}
         
            </div>

            {/* Share Modal */}
            {showShareModal && pin && (
              <ShareModal
                pin={pin}
                onClose={() => setShowShareModal(false)}
              />
            )}

            {/* Share Dropdown Menu with better design */}
            {showShareMenu && (
              <div className="absolute top-24 right-6 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl py-2 min-w-[240px] z-[9999] border border-gray-200 dark:border-gray-700 animate-scale-in">
                <button onClick={shareToFacebook} className="w-full px-5 py-3 hover:bg-blue-50 dark:hover:bg-blue-900/20 flex items-center gap-3 text-left transition-all group">
                  <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <span className="material-symbols-rounded text-[20px] text-blue-600">share</span>
                  </div>
                  <span className="font-semibold text-sm text-gray-900 dark:text-gray-100">Facebook</span>
                </button>
                <button onClick={shareToTwitter} className="w-full px-5 py-3 hover:bg-sky-50 dark:hover:bg-sky-900/20 flex items-center gap-3 text-left transition-all group">
                  <div className="w-10 h-10 rounded-full bg-sky-100 dark:bg-sky-900/30 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <span className="material-symbols-rounded text-[20px] text-sky-500">share</span>
                  </div>
                  <span className="font-semibold text-sm text-gray-900 dark:text-gray-100">Twitter</span>
                </button>
                <button onClick={shareToLinkedIn} className="w-full px-5 py-3 hover:bg-blue-50 dark:hover:bg-blue-900/20 flex items-center gap-3 text-left transition-all group">
                  <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <span className="material-symbols-rounded text-[20px] text-blue-700">share</span>
                  </div>
                  <span className="font-semibold text-sm text-gray-900 dark:text-gray-100">LinkedIn</span>
                </button>
                <div className="border-t border-gray-200 dark:border-gray-700 my-2"></div>
                <button onClick={copyShareLink} className="w-full px-5 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-3 text-left transition-all group">
                  <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <span className="material-symbols-rounded text-[20px] text-gray-600 dark:text-gray-400">link</span>
                  </div>
                  <span className="font-semibold text-sm text-gray-900 dark:text-gray-100">Sao chép link</span>
                </button>
              </div>
            )}

            {/* Owner Actions with better design */}
            {isOwner && (
              <div className="mb-6 pb-6 border-b border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => window.location.href = `/edit/${currentPinId}`}
                  className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white transition-all hover:scale-105 shadow-lg shadow-blue-500/30"
                >
                  <span className="material-symbols-rounded text-[20px]">edit</span>
                  <span className="font-bold">Chỉnh sửa Prompt</span>
                </button>
              </div>
            )}

            {/* Title with gradient */}
            <div className="mb-6">
              <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-400 bg-clip-text text-transparent mb-4 leading-tight">
                {pin.title}
              </h2>

              {/* Board badge */}
              {pin.board && (
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-100 to-blue-100 dark:from-purple-900/30 dark:to-blue-900/30 rounded-full border border-purple-200 dark:border-purple-800">
                  <span className="material-symbols-rounded text-[18px] text-purple-600 dark:text-purple-400">
                    {pin.board.icon || 'folder'}
                  </span>
                  <span className="font-semibold text-sm text-purple-900 dark:text-purple-200">{pin.board.name}</span>
                </div>
              )}
            </div>

            {/* Prompt with better design */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Prompt</h3>
                <button
                  onClick={copyPrompt}
                  className="text-xs font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 flex items-center gap-1 transition-colors"
                >
                  <span className="material-symbols-rounded text-[16px]">content_copy</span>
                  Copy
                </button>
              </div>
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 p-5 rounded-2xl relative group border border-gray-200 dark:border-gray-700 shadow-inner">
                <p className="text-gray-900 dark:text-gray-100 text-[15px] font-mono leading-relaxed whitespace-pre-wrap">
                  {pin.prompt}
                </p>
              </div>
            </div>

            {/* Reference Hint with icon */}
            {pin.refHint && (
              <div className="mb-6">
                <h3 className="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3 flex items-center gap-2">
                  <span className="material-symbols-rounded text-amber-500 text-[20px] animate-pulse-slow">lightbulb</span>
                  Gợi ý ảnh tham khảo
                </h3>
                <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border-2 border-amber-200 dark:border-amber-800 p-5 rounded-2xl shadow-sm">
                  <p className="text-amber-900 dark:text-amber-200 text-[14px] leading-relaxed">
                    {pin.refHint}
                  </p>
                </div>
              </div>
            )}

            {/* Tags with better design */}
            {pin.tags && pin.tags.length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">Tags</h3>
                <div className="flex flex-wrap gap-2">
                  {pin.tags.map((tag, index) => (
                    <span
                      key={index}
                      onClick={() => filterByTag(tag)}
                      className="group px-4 py-2 bg-gradient-to-r from-gray-900 to-gray-700 hover:from-blue-600 hover:to-purple-600 dark:from-gray-700 dark:to-gray-600 dark:hover:from-blue-600 dark:hover:to-purple-600 text-white text-sm font-medium rounded-full cursor-pointer transition-all hover:scale-110 shadow-md hover:shadow-lg"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Comments Section with better design */}
            <div className="mb-6 border-t-2 border-gray-200 dark:border-gray-700 pt-6">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <span className="material-symbols-rounded text-[24px] text-blue-600">comment</span>
                Bình luận ({comments.length})
              </h3>

              {/* Comments List */}
              <div className="space-y-4 mb-5 max-h-[300px] overflow-y-auto custom-scrollbar">
                {!commentsEnabled ? (
                  <div className="text-center py-8 bg-gray-50 dark:bg-gray-800 rounded-2xl">
                    <span className="material-symbols-rounded text-4xl text-gray-300 dark:text-gray-600 mb-2 block">comments_disabled</span>
                    <p className="text-gray-500 dark:text-gray-400 text-sm">Bình luận đã bị tắt</p>
                  </div>
                ) : comments.length === 0 ? (
                  <div className="text-center py-8 bg-gray-50 dark:bg-gray-800 rounded-2xl">
                    <span className="material-symbols-rounded text-4xl text-gray-300 dark:text-gray-600 mb-2 block">chat_bubble_outline</span>
                    <p className="text-gray-500 dark:text-gray-400 text-sm">Chưa có bình luận nào</p>
                  </div>
                ) : (
                  comments.map((comment) => {
                    const isCommentOwner = currentUser && currentUser.username === comment.user;
                    return (
                      <div key={comment._id} className="flex gap-3 animate-slide-in-left">
                        <img
                          src={comment.userData?.avatar || `https://api.dicebear.com/7.x/notionists/svg?seed=${comment.user}&backgroundColor=e9e9e9`}
                          className="w-10 h-10 rounded-full bg-gray-100 shrink-0 cursor-pointer hover:opacity-80 transition-all hover:scale-110 ring-2 ring-transparent hover:ring-blue-500"
                          alt={comment.user}
                          onClick={() => {
                            onClose();
                            navigate(`/user/${comment.user}`);
                          }}
                        />
                        <div className="flex-1">
                          <div className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 rounded-2xl px-4 py-3 border border-gray-200 dark:border-gray-700">
                            <p 
                              className="font-bold text-sm text-gray-900 dark:text-gray-100 cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                              onClick={() => {
                                onClose();
                                navigate(`/user/${comment.user}`);
                              }}
                            >
                              {comment.userData?.displayName || comment.user}
                            </p>
                            <p className="text-sm text-gray-700 dark:text-gray-300 mt-1 leading-relaxed">{comment.text}</p>
                            {comment.isViolation && (
                              <p className="text-xs text-red-600 mt-2 flex items-center gap-1">
                                <span className="material-symbols-rounded text-[14px]">warning</span>
                                {comment.violationReason}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-3 mt-2 ml-4">
                            <p className="text-xs text-gray-400">{formatTimeAgo(comment.createdAt)}</p>
                            {isCommentOwner && (
                              <button 
                                onClick={() => handleDeleteComment(comment._id)}
                                className="text-xs text-red-600 hover:text-red-700 font-semibold hover:underline"
                              >
                                Xóa
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {/* Add Comment Form */}
              {isAuthenticated && commentsEnabled && (
                <form onSubmit={handleAddComment} className="flex gap-3 items-start">
                  <img
                    src={currentUser?.avatar || `https://api.dicebear.com/7.x/notionists/svg?seed=${currentUser?.username}&backgroundColor=e9e9e9`}
                    className="w-10 h-10 rounded-full bg-gray-100 shrink-0 ring-2 ring-blue-500"
                    alt="Current user"
                  />
                  <div className="flex-1">
                    <textarea
                      value={commentText}
                      onChange={(e) => setCommentText(e.target.value)}
                      placeholder="Viết bình luận của bạn..."
                      className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-700 rounded-2xl resize-none focus:outline-none focus:border-blue-500 dark:focus:border-blue-500 transition-colors text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                      rows="3"
                    />
                    <button
                      type="submit"
                      className="mt-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-6 py-2.5 rounded-full font-bold text-sm transition-all hover:scale-105 shadow-lg shadow-blue-500/30"
                    >
                      Đăng bình luận
                    </button>
                  </div>
                </form>
              )}
            </div>

            {/* User Info with better design */}
            <div className="mt-6 border-t-2 border-gray-200 dark:border-gray-700 pt-6">
              <div className="flex items-center justify-between mb-5">
                <div 
                  className="flex items-center gap-3 cursor-pointer group"
                  onClick={() => {
                    onClose();
                    navigate(`/user/${pin.user}`);
                  }}
                >
                  <img
                    src={`https://api.dicebear.com/7.x/notionists/svg?seed=${pin.user}&backgroundColor=e9e9e9`}
                    className="w-14 h-14 rounded-full bg-gray-100 ring-4 ring-gray-200 dark:ring-gray-700 group-hover:ring-blue-500 transition-all group-hover:scale-110"
                    alt={pin.user}
                  />
                  <div className="min-w-0 flex-1">
                    <p className="font-bold text-base text-gray-900 dark:text-gray-100 truncate group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                      {pin.user}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Tác giả
                    </p>
                  </div>
                </div>
                {!isOwner && isAuthenticated && (
                  <button className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-5 py-2.5 rounded-full font-bold text-sm transition-all hover:scale-105 shadow-lg shadow-blue-500/30 shrink-0">
                    Theo dõi
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Custom scrollbar styles */}
      <style dangerouslySetInnerHTML={{__html: `
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
        .dark .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #475569;
        }
        .dark .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #64748b;
        }
      `}} />
    </div>
  );
}

export default PinModal;
